// What a built step already tells the person (2026-10-29, Jenn; docs/agent-draft-shadow.md
// "What's next lists only what the CSR wouldn't otherwise see";
// docs/decisions/2026-10-29-whats-next-only-what-the-csr-cannot-see.md).
//
// Two uses, one map (STEP_COVERS, step kind -> the phrases it covers):
//   1. What's next drops an item a step already tells the person to do
//      (withoutCoveredNextSteps()): tracking a return label and choosing the
//      stage when the Return Status stage choice is there, checking the
//      photos when the review-images step is there. Items that say the same
//      thing as each other become one (mergeSameNextSteps()).
//   2. An agent's free-text step ("other", or an advice action with no
//      builder) about what a built step does is not its own step: its
//      sentence goes in that step's sources (coveringStepIndex()), as the
//      merged steps already do (shadow-step-previews.js's preview.sources).
//      Used by the agent panel's step previews and by the primary draft's
//      steps (server/src/ai/agent-draft-primary.js).
//
// A step's kind (coverKindOf()) is read from a pipeline step (proposed
// action), an agent action, or a shadow step preview, so the server and the
// UI ask the same question. Pure: no calls.

export const COVER_RETURN_STAGE = 'return_stage'
export const COVER_REVIEW_IMAGES = 'review_images'

// Phrases per kind. A return stage: the return's status or stage, choosing
// in transit / delivered, tracking the return (its label, the customer's
// return, the package coming back), or finding how it was shipped.
const RETURN_STAGE_PHRASES = Object.freeze([
  /\breturn(?:'s|’s)?\s+(?:status|stage)\b/i,
  /\bstatus\s+of\s+the\s+return\b/i,
  /\bin[\s-]transit\b[^.\n]{0,60}\bdelivered\b|\bdelivered\b[^.\n]{0,60}\bin[\s-]transit\b/i,
  /\btrack(?:ing)?\b[^.\n]{0,80}\breturn\b|\breturn\b[^.\n]{0,40}\btrack(?:ing)?\b/i,
  /\bhow the customer shipped the return\b/i,
  /\bwhere the return is\b|\bwhere is the return\b/i,
])
const REVIEW_IMAGES_PHRASES = Object.freeze([
  /\b(?:check|review|look at|inspect|open)\b[^.\n]{0,40}\b(?:photos?|images?|pictures?|pics)\b/i,
])
export const STEP_COVERS = Object.freeze({
  [COVER_RETURN_STAGE]: RETURN_STAGE_PHRASES,
  [COVER_REVIEW_IMAGES]: REVIEW_IMAGES_PHRASES,
})

const REVIEW_IMAGES_TYPES = new Set(['review_customer_images', 'review_images'])

// The kind a step covers, or null. step: a pipeline step ({ type, params }),
// an agent action ({ type, stage }) or a step preview ({ kind, type }).
export function coverKindOf(step) {
  if (!step || typeof step !== 'object') return null
  const type = String(step.type ?? '')
  if (type === 'return_stage_choice') return COVER_RETURN_STAGE
  // The pipeline's step (params.kind) or the agent panel's preview of it
  // (options.stageChoice).
  if (type === 'task_path_choice' && (step.params?.kind === 'return_stage' || step.options?.stageChoice === true)) return COVER_RETURN_STAGE
  if (REVIEW_IMAGES_TYPES.has(type)) return COVER_REVIEW_IMAGES
  return null
}

// The kinds a list of steps covers.
export function coverKindsOf(steps = []) {
  const out = new Set()
  for (const s of Array.isArray(steps) ? steps : []) {
    const k = coverKindOf(s)
    if (k) out.add(k)
  }
  return out
}

// The kind (of kinds) that covers a sentence, or null. rma: a return
// document number a return-stage step is about (the same RMA counts).
export function sentenceCoveredBy(text, kinds, { rma = null } = {}) {
  const s = String(text ?? '')
  if (!s.trim()) return null
  for (const kind of kinds instanceof Set ? kinds : new Set(kinds ?? [])) {
    if ((STEP_COVERS[kind] ?? []).some((re) => re.test(s))) return kind
    if (kind === COVER_RETURN_STAGE && rma && new RegExp(`\\b${String(rma).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(s)) return kind
  }
  return null
}

// What's next without the items a step already covers. kinds: from
// coverKindsOf() of the steps shown.
// A check code wrote about the reply ("Check: ...", "Rewrite the reply
// ...") is never covered by a step: it is about the reply, not a task.
const REPLY_CHECK_RE = /^\s*(?:Check:|Rewrite the reply|The reply)/i
export function withoutCoveredNextSteps(nextSteps = [], kinds = []) {
  const set = kinds instanceof Set ? kinds : new Set(kinds ?? [])
  if (!set.size) return [...(nextSteps ?? [])]
  return (nextSteps ?? []).filter((s) => REPLY_CHECK_RE.test(String(s ?? '')) || !sentenceCoveredBy(s, set))
}

// A tracking number: UPS 1Z..., Amazon TBA..., or 12+ digits.
const TRACKING_RE = /\b(?:1Z[0-9A-Z]{16}|TBA\d{6,}|\d{12,34})\b/gi
const TRACK_LINE_RE = /^\s*(?:track|check)\b/i

// What's next items that say the same thing become one, the first kept: the
// return-tracking lines (track the return label, track the customer's
// return, choose in transit or delivered), and carrier-check lines naming
// the same tracking numbers as an earlier one.
export function mergeSameNextSteps(nextSteps = []) {
  const out = []
  let returnTopic = false
  const seenNumbers = new Set()
  for (const raw of nextSteps ?? []) {
    const s = String(raw ?? '')
    if (!s.trim() || out.includes(s)) continue
    const isReturn = !REPLY_CHECK_RE.test(s) && RETURN_STAGE_PHRASES.some((re) => re.test(s))
    const numbers = TRACK_LINE_RE.test(s) ? (s.match(TRACKING_RE) ?? []).map((n) => n.toUpperCase()) : []
    if (isReturn && returnTopic) continue
    if (numbers.length && numbers.every((n) => seenNumbers.has(n))) continue
    if (isReturn) returnTopic = true
    for (const n of numbers) seenNumbers.add(n)
    out.push(s)
  }
  return out
}

// Both: covered items dropped, then the rest merged.
export function whatsNextOnly(nextSteps = [], steps = []) {
  return mergeSameNextSteps(withoutCoveredNextSteps(nextSteps, coverKindsOf(steps)))
}

// The free-text steps an agent writes: "other", or an advice action
// (shadow-step-previews.js's PREVIEW_BUILDERS has no builder for it).
export function isFreeTextStep(step) {
  return !step?.type || step.type === 'other'
}

// The index of the step (of steps) whose kind covers a free-text sentence,
// or -1. rmaOf(step): the return document a step is about, if any.
export function coveringStepIndex(text, steps = [], { rmaOf = () => null } = {}) {
  const list = Array.isArray(steps) ? steps : []
  for (let i = 0; i < list.length; i++) {
    const kind = coverKindOf(list[i])
    if (kind && sentenceCoveredBy(text, [kind], { rma: rmaOf(list[i]) })) return i
  }
  return -1
}
